import { Injectable } from '@angular/core';
import * as Pusher from 'pusher-js';
import { environment } from 'src/environments/environment';
import { AppRestEndPoint } from '../app-restEndPoint';

@Injectable({
	providedIn: 'root'
})
export class PusherService {

	pusherObj: any;
	pusherDeactivateObj: any;
	/** Cached env values — pulled once so we don't repeatedly hit the imported
	 *  module reference (and so a missing value surfaces as a clear console
	 *  warning instead of stringifying to "undefined_user_123" channel names). */
	private readonly envName: string = (environment as any)?.environment ?? '';
	private readonly pusherKey: string = (environment as any)?.pusher_app_key ?? '';
	private readonly pusherCluster: string = (environment as any)?.pusher_app_cluster ?? '';

	constructor() {
		if (!this.pusherKey || !this.pusherCluster) {
			console.error('[PusherService] Missing pusher_app_key / pusher_app_cluster in environment — Pusher will not initialise.', {
				envName: this.envName,
				hasKey: !!this.pusherKey,
				hasCluster: !!this.pusherCluster,
			});
			return;
		}
		this.pusherObj = new Pusher(this.pusherKey, {
			cluster: this.pusherCluster,
			encrypted: true,
			// DEV-278: dynamic authorizer for Pusher PRIVATE channels. The access
			// token is read FRESH from sessionStorage at subscribe time (this
			// singleton is constructed before login, so a static authEndpoint token
			// would be empty). Public channels never invoke this.
			authorizer: (channel: any) => ({
				authorize: (socketId: string, callback: Function) => {
					this.authorizePrivateChannel(channel.name, socketId, callback);
				}
			})
		} as any);
		// Previously this was `this.pusherDeactivate = new Pusher(...)` which
		// overwrote the `pusherDeactivate` method declared below with a Pusher
		// instance — leaving any caller of `pusherDeactivate()` to crash. Assign
		// to the dedicated `pusherDeactivateObj` field instead.
		this.pusherDeactivateObj = new Pusher(this.pusherKey + "xx", { cluster: this.pusherCluster, encrypted: true });
	}

	pusher = function () {
		return this.pusherObj;
	}

	pusherDeactivate = function () {
		console.log('Calling the deactivated pusher' + this.pusherDeactivateObj);
		return this.pusherDeactivateObj;
	}

	bind = function (channel, func) {
		console.log('Calling the original pusher' + this.pusherObj);
		this.pusherObj.unbind(channel);
		this.pusherObj.bind(channel, func);
	}

	/**
	 * Drop every handler for one event name. Components that bind an event for the
	 * lifetime of a modal must call this on destroy: `bind()` unbinds first, so a
	 * handler left behind would either be silently replaced by the next binder or keep
	 * firing against a destroyed component. Only unbind events you own — the dashboard
	 * keeps long-lived handlers on the bank refresh progress events.
	 */
	unbind = function (event) {
		if (!this.pusherObj) {
			return;
		}
		this.pusherObj.unbind(event);
	}


	subscribeAuthyServer = function (authyId) {
		this.unsub(this.envName + "_user_" + authyId);
	}

	subscribeBankRefresh = function (household_id) {
		this.unsub(household_id);
	}

	subscribeDocusign = function (household_id) {
		this.unsub(this.envName + "_household_" + household_id);
	}

	// DEV-278: PRIVATE channel — subscription is authorized by the apiclient
	// pusher/auth endpoint (which signs only the requesting user's own channel).
	subscribePhilanthropicProfileStatement = function (user_id) {
		this.unsub("private-" + this.envName + "_philanthropic_profile_" + user_id);
	}


	unsub(channel: string | number) {
		if (!channel && channel !== 0) {
			console.error('Invalid channel name provided to unsub:', channel);
			return;
		}
		if (!this.pusherObj) {
			console.error('[PusherService] pusherObj not initialised — refusing to subscribe to', channel);
			return;
		}
		// Convert to string if it's a number
		const channelName = typeof channel === 'string' ? channel : String(channel);
		try {
			this.pusherObj.unsubscribe(channelName);
			this.pusherObj.subscribe(channelName);
		} catch (error) {
			console.error('Error in unsub for channel:', channelName, error);
		}
	}

	// DEV-278: Pusher private-channel auth handshake. POSTs the socket_id +
	// channel_name to the apiclient pusher/auth endpoint with the current access
	// token (query param, matching ApiService) and hands Pusher the signed auth
	// payload. Routed through AppRestEndPoint.CURRENT_API_URL so it follows the
	// same per-host API base the rest of the app uses.
	private authorizePrivateChannel(channelName: string, socketId: string, callback: Function): void {
		const token = sessionStorage.getItem('token') || '';
		const url = AppRestEndPoint.CURRENT_API_URL + '/apiclient/v1/pusher/auth?access_token=' + encodeURIComponent(token);
		fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
			body: 'socket_id=' + encodeURIComponent(socketId) + '&channel_name=' + encodeURIComponent(channelName),
		})
			.then(res => res.ok ? res.json() : Promise.reject(res.status))
			.then(data => callback(null, data))
			.catch(err => {
				console.error('[PusherService] private channel auth failed for', channelName, err);
				callback(new Error('Pusher auth failed'), null);
			});
	}
}
